import { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Video, VideoOff, Square, RotateCcw, AlertCircle } from 'lucide-react';
import useVideoDurationFix from '../../../hooks/useVideoDurationFix';

/**
 * VideoRecorder — webcam + mic kayıt component
 *
 * Props:
 *  isRecording      — boolean
 *  blobUrl          — string | null (recorded video)
 *  duration         — number (seconds)
 *  permissionDenied — boolean
 *  error            — string | null
 *  stream           — MediaStream | null (for live preview)
 *  onStart          — function
 *  onStop           — function
 *  onReset          — function
 *  labelRecord      — string
 *  labelStop        — string
 *  labelRetry       — string
 *  labelPermDenied  — string
 *  videoClassName   — string | undefined
 *    Opsiyonel: video sarıcı div'inin sınıfını override eder.
 *    Default: 'aspect-video' (geriye dönük uyumlu).
 *    RECORDING step gibi kısa-ekran senaryolarında parent 'h-full' geçerek
 *    16:9 yükseklik zorlamasını kaldırır; video object-cover ile kırpılır ama taşmaz.
 */
export default function VideoRecorder({
  isRecording,
  blobUrl,
  duration = 0,
  permissionDenied = false,
  error = null,
  stream = null,
  onStart,
  onStop,
  onReset,
  labelRecord = 'Kayıt Başlat',
  labelStop = 'Durdur',
  labelRetry = 'Tekrar Kaydet',
  labelPermDenied = 'Kamera ve mikrofona erişim izni gerekli',
  /** hideControls: omit action buttons entirely (e.g. when parent supplies its own stop/retry UI) */
  hideControls = false,
  /**
   * videoClassName — video sarıcı div'inin aspect/yükseklik sınıfı.
   * Default 'aspect-video' (mevcut davranış, geriye dönük uyumlu).
   * RECORDING step'te CabinInterviewSimulation 'h-full' geçer.
   */
  videoClassName,
}) {
  const videoRef = useRef(null);
  const playbackRef = useRef(null);

  // Duration fix: WebM Infinity-duration sorununu gider (sadece playback için)
  useVideoDurationFix(playbackRef, blobUrl);

  // Live preview
  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }
  }, [stream]);

  // Playback
  useEffect(() => {
    if (playbackRef.current && blobUrl) {
      playbackRef.current.src = blobUrl;
    }
  }, [blobUrl]);

  const formatTime = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  if (permissionDenied) {
    return (
      <div className="flex flex-col items-center gap-3 p-6 bg-rose-50 border-2 border-rose-200 rounded-2xl text-center">
        <AlertCircle size={28} className="text-rose-500" aria-hidden="true" />
        <p className="text-sm text-rose-700 font-medium">{labelPermDenied}</p>
      </div>
    );
  }

  // videoClassName prop geçildiğinde (RECORDING: h-full) dış sarıcı da h-full alır;
  // böylece parent flex-1 min-h-0 içinde video tam yüksekliği doldurur.
  // Prop geçilmezse (PREVIEW / standalone) eski davranış: aspect-video, sarıcı min-h auto.
  return (
    <div className={`flex flex-col gap-4 ${videoClassName ? 'h-full' : ''}`}>
      {/* Video area */}
      {/* videoClassName prop: default 'aspect-video' (geriye dönük uyumlu); kısa ekranlarda 'h-full' geçilir */}
      <div className={`relative bg-gray-900 rounded-2xl overflow-hidden border-2 border-gray-700 ${videoClassName ?? 'aspect-video'}`}>
        {/* Live preview */}
        {!blobUrl && (
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            className="w-full h-full object-cover"
            aria-label="Kamera önizlemesi"
          />
        )}

        {/* Playback */}
        {blobUrl && !isRecording && (
          <video
            ref={playbackRef}
            controls
            playsInline
            className="w-full h-full object-cover"
            aria-label="Video önizlemesi"
          />
        )}

        {/* Recording indicator overlay */}
        {isRecording && (
          <div className="absolute top-3 left-3 flex items-center gap-2 bg-black/60 rounded-lg px-3 py-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-rose-500 animate-pulse" aria-hidden="true" />
            <span className="text-white text-xs font-mono font-bold" aria-live="polite">
              {formatTime(duration)}
            </span>
          </div>
        )}

        {/* No stream fallback */}
        {!stream && !blobUrl && (
          <div className="absolute inset-0 flex items-center justify-center">
            <VideoOff size={40} className="text-gray-600" aria-hidden="true" />
          </div>
        )}
      </div>

      {/* Controls — hidden when parent supplies its own action buttons */}
      {!hideControls && (
        <div className="flex items-center justify-center gap-4">
          {!blobUrl ? (
            <motion.button
              type="button"
              onClick={isRecording ? onStop : onStart}
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.96 }}
              className={`
                flex items-center gap-2 px-6 py-3 rounded-xl border-2 font-bold text-sm transition-colors
                focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]
                ${isRecording
                  ? 'bg-rose-500 border-rose-700 text-white hover:bg-rose-600'
                  : 'bg-cyan-500 border-cyan-700 text-white hover:bg-cyan-600'
                }
              `}
            >
              {isRecording ? (
                <><Square size={16} aria-hidden="true" /> {labelStop}</>
              ) : (
                <><Video size={16} aria-hidden="true" /> {labelRecord}</>
              )}
            </motion.button>
          ) : (
            <motion.button
              type="button"
              onClick={onReset}
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.96 }}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 border-gray-300 text-gray-600 font-semibold text-sm hover:bg-gray-50 transition-colors"
            >
              <RotateCcw size={15} aria-hidden="true" />
              {labelRetry}
            </motion.button>
          )}
        </div>
      )}

      {error && !permissionDenied && (
        <p className="text-xs text-rose-600 text-center" role="alert">{error}</p>
      )}
    </div>
  );
}
